import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getProfiles, deleteProfile } from '../../actions/profile';
import { Plus, Search, Edit, Trash2, Users, Settings, AlertTriangle } from 'lucide-react';

const Profiles = ({ getProfiles, deleteProfile, profiles, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState(null);
    const [filteredProfiles, setFilteredProfiles] = useState([]);

    useEffect(() => {
        getProfiles();
    }, []);

    useEffect(() => {
        if (profiles) {
            const filtered = profiles.filter(profile =>
                profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                profile.profileType?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredProfiles(filtered);
        }
    }, [profiles, searchTerm]);

    const handleDeleteClick = (profile) => {
        setProfileToDelete(profile);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (profileToDelete) {
            deleteProfile(profileToDelete._id);
            setShowDeleteModal(false);
            setProfileToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setProfileToDelete(null);
    };

    const getProfileTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'live': return 'success';
            case 'test': return 'warning';
            case 'development': return 'info';
            default: return 'secondary';
        }
    };

    const getTotalPackages = (profile) => {
        let total = 0;
        ['welcome', 'popular', 'current', 'accessories', 'refundableDeposit'].forEach(category => {
            if (profile[category]?.bundles) {
                total += profile[category].bundles.length;
            }
        });
        return total;
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <Users size={32} className="me-3 text-primary" />
                                    Product Profiles
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage your product profiles and configurations
                                </p>
                            </div>
                            <Link 
                                to="/inventory/v1.2/profiles/new"
                                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Create New Profile
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Search and Stats Section */}
                <div className="row mb-4">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search profiles by name or type..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-sm bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{profiles?.length || 0}</div>
                                        <div className="small">Total Profiles</div>
                                    </div>
                                    <Settings size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Profiles Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading profiles...</p>
                    </div>
                ) : filteredProfiles && filteredProfiles.length > 0 ? (
                    <div className="row">
                        {filteredProfiles.map((profile) => (
                                                         <div key={profile._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                <div 
                                    className="card shadow-sm border-0 h-100" 
                                    style={{
                                        borderRadius: '0.75rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-header bg-white border-bottom-0 pb-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title mb-1 text-primary fw-bold">
                                                    {profile.name}
                                                </h5>
                                                <span 
                                                    className={`badge bg-${getProfileTypeColor(profile.profileType)} mb-2`}
                                                    style={{ fontSize: '0.75rem' }}
                                                >
                                                    {profile.profileType || 'Default'}
                                                </span>
                                            </div>
                                            <div className="btn-group">
                                                <Link 
                                                    to={`/inventory/v1.2/profiles/${profile._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Edit Profile"
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteClick(profile)}
                                                    title="Delete Profile"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body pt-2">
                                        <div className="row g-3">
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-info">{profile.cansLimit || 0}</div>
                                                    <div className="small text-muted">Cans Limit</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-success">{getTotalPackages(profile)}</div>
                                                    <div className="small text-muted">Total Packages</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-warning">{profile.banner?.length || 0}</div>
                                                    <div className="small text-muted">Banners</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h5 mb-0 text-secondary">
                                                        {profile.membership ? '✓' : '✗'}
                                                    </div>
                                                    <div className="small text-muted">Membership</div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Category Summary */}
                                        <div className="mt-3 pt-3 border-top">
                                            <div className="small text-muted mb-2">Categories:</div>
                                            <div className="d-flex flex-wrap gap-1">
                                                {['welcome', 'popular', 'current', 'accessories', 'refundableDeposit'].map(category => {
                                                    const count = profile[category]?.bundles?.length || 0;
                                                    return count > 0 ? (
                                                        <span key={category} className="badge bg-light text-dark">
                                                            {category}: {count}
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-light border-top-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Link 
                                                to={`/inventory/v1.2/profiles/${profile._id}`}
                                                className="btn btn-primary btn-sm"
                                            >
                                                <Edit size={14} className="me-1" />
                                                Manage
                                            </Link>
                                            <small className="text-muted">
                                                ID: {profile._id?.slice(-6)}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <Users size={64} className="text-muted" />
                        </div>
                        <h4 className="text-muted mb-3">
                            {searchTerm ? 'No profiles found' : 'No profiles created yet'}
                        </h4>
                        <p className="text-muted mb-4">
                            {searchTerm 
                                ? `No profiles match "${searchTerm}". Try adjusting your search.`
                                : 'Get started by creating your first product profile.'
                            }
                        </p>
                        {!searchTerm && (
            <Link 
                to="/inventory/v1.2/profiles/new"
                                className="btn btn-primary btn-lg"
                            >
                                <Plus size={20} className="me-2" />
                                Create Your First Profile
            </Link>
                        )}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title d-flex align-items-center text-danger">
                                        <AlertTriangle size={24} className="me-2" />
                                        Confirm Delete
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={cancelDelete}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <Trash2 size={48} className="text-danger" />
                                        </div>
                                        <h6 className="mb-3">Delete Profile "{profileToDelete?.name}"?</h6>
                                        <p className="text-muted mb-4">
                                            This action cannot be undone. All associated data including packages, 
                                            banners, and configurations will be permanently deleted.
                                        </p>
                                        <div className="alert alert-warning d-flex align-items-center">
                                            <AlertTriangle size={20} className="me-2" />
                                            <small>
                                                <strong>Warning:</strong> This will permanently delete the profile and all its data.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={cancelDelete}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                    >
                                        <Trash2 size={16} className="me-2" />
                                        Delete Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>


        </Fragment>
    );
};

Profiles.propTypes = {
    profiles: PropTypes.array,
    getProfiles: PropTypes.func.isRequired,
    deleteProfile: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

const mapStateToProps = state => ({
    profiles: state.profile.profiles,
    loading: state.profile.loading
});

export default connect(mapStateToProps, { getProfiles, deleteProfile })(Profiles);

